import { useMemo, useState } from 'react';

import { AiContext } from '@/components/ai-context';
import { useResolvedInstructionIds } from '@/hooks/rxdb-models';
import { Tab } from '@flintmint/web-shared/components/tab';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Tabs } from '@flintmint/web-shared/components/tabs';
import { Switch } from '@flintmint/web-shared/components/switch';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { EditorSidebarHolder } from '@/components/editor-sidebar-holder';
import { DocuAiDockSlot } from '@/features/layout/docuai-dock-slot';
import { TabPanels } from '@flintmint/web-shared/components/tab-panels';
import { TabPanel } from '@flintmint/web-shared/components/tab-panel';

import styles from '../block-editor-overlay.module.scss';

export const BlockEditorSidebar = (props: BlockEditorSidebarProps) => {
  const {
    onInstructionCreate,
    onInstructionDelete,
    onInstructionEnabledChange,
    blockInstructions,
    attachedToNote,
    autoApplyChanges,
    applyChangesToNote,
    onApplyChangesToNote,
  } = props;

  const [activeTab, setActiveTab] = useState('aiContext');

  const instructionIds = useMemo(() => Object.keys(blockInstructions || {}), [blockInstructions]);
  const resolvedInstructionIds = useResolvedInstructionIds(instructionIds);
  const instructionCount = useMemo(
    () => ({
      total: resolvedInstructionIds.length,
      enabled: resolvedInstructionIds.filter((id) => blockInstructions?.[id]).length,
    }),
    [resolvedInstructionIds, blockInstructions]
  );

  return (
    <div
      className={styles.blockEditorSidebarHolder}
      data-testid="block-editor-sidebar"
      data-floating-chat-dock-column="block-editor"
    >
      {attachedToNote && !autoApplyChanges && (
        <div className={styles.applyChanges}>
          <div>
            <Switch
              checked={applyChangesToNote}
              onChange={onApplyChangesToNote}
              data-testid="apply-changes-switch"
              label={
                <div className={styles.applyChangesInfoTooltip}>
                  <span>Apply changes to the Note</span>
                  <Tooltip
                    className={styles.applyChangesInfo}
                    content="When enabled the changes will be applied from Master Block to your local block in the Note."
                  >
                    <span className={styles.infoIcon}>
                      <Icon type="information-circle-solid" size={16} />
                    </span>
                  </Tooltip>
                </div>
              }
            />
          </div>
        </div>
      )}
      <EditorSidebarHolder className={styles.sidebarContent}>
        <Tabs value={activeTab} appearance="elevated" onChange={(tabId) => setActiveTab(tabId)}>
          <Tab
            id="aiContext"
            name="Ai Context"
            icon={<Icon size={20} type="ai-brain-01-twotone" multiColor />}
            data-testid="block-ai-context"
          />
        </Tabs>
        <TabPanels value={activeTab} className={styles.panels}>
          <TabPanel id="aiContext">
            <AiContext
              instructionCount={instructionCount}
              documentInstructions={blockInstructions}
              onInstructionCreate={onInstructionCreate}
              onInstructionDelete={onInstructionDelete}
              onInstructionEnabledChange={onInstructionEnabledChange}
              instructionEditorTestId="block-instruction-editor"
              view="block"
            />
          </TabPanel>
        </TabPanels>
      </EditorSidebarHolder>
      <DocuAiDockSlot column="block-editor" className={styles.dockSlot} />
    </div>
  );
};

export interface BlockEditorSidebarProps {
  attachedToNote: boolean;
  autoApplyChanges: boolean;
  applyChangesToNote: boolean;
  onApplyChangesToNote: () => void;
  blockInstructions: Record<string, boolean>;
  onInstructionCreate: (instructionId: string) => void;
  onInstructionDelete?: (instructionId: string) => void | Promise<void>;
  onInstructionEnabledChange: (instructionId: string, enabled: boolean) => void;
}
